import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";
import { GameIcon } from "./GameIcon";
import { ScreenTitle, type ScreenTitleIcon } from "./ScreenTitle";

/** Felt radial gradient shared by the redesigned Lobby/Profile screens. */
const FELT_BG =
  "radial-gradient(circle at top, rgb(var(--c-room-light) / 0.8) 0%, rgb(var(--c-surface)) 80%)";

/**
 * The page shell for the lobby-level drawer destinations (Messages / Store /
 * Settings / Guides / History / another user's Profile). Mirrors the top-tab
 * headers: a sticky app bar with a matching badge-style **back button** on the
 * inline-start (top-RIGHT in RTL) and a centered {@link ScreenTitle} — a gold
 * icon badge + heading — so every drawer screen reads as one family with the
 * گفتگو/دوستان/پروفایل tabs. Renders inside App's scroll container; the shared
 * BottomNav sits below it.
 */
export function PageShell({
  title,
  icon,
  children,
  onBack,
}: {
  title: string;
  /** The gold badge glyph beside the title (matches the top tabs). Omit for a bare title. */
  icon?: ScreenTitleIcon;
  children: ReactNode;
  /** Back-button handler; defaults to returning to the lobby. */
  onBack?: () => void;
}): ReactNode {
  const setView = useSession((s) => s.setView);
  return (
    <div
      className="flex min-h-full flex-col bg-background text-on-surface"
      style={{ backgroundImage: FELT_BG }}
      dir="rtl"
    >
      <header
        className="sticky top-0 z-20 flex items-center gap-2 px-md h-16 bg-surface/90 border-b border-outline-variant/30"
        style={{ backdropFilter: "blur(12px)" }}
      >
        {/* Back — inline-start (top-right in RTL); a rounded badge matching the title glyph's
            shape/size so the two read as a pair. The chevron already points right (RTL back). */}
        <button
          type="button"
          aria-label={fa.common.back}
          onClick={() => (onBack ? onBack() : setView("lobby"))}
          className="flex h-9 w-9 flex-none appearance-none items-center justify-center rounded-xl border border-outline-variant/70 bg-surface-container-high cursor-pointer text-on-surface shadow-sm transition-transform active:scale-90 hover:bg-surface-variant"
        >
          <GameIcon type="back" size={20} />
        </button>
        <div className="flex min-w-0 flex-1 items-center justify-center">
          {icon ? (
            <ScreenTitle icon={icon} title={title} />
          ) : (
            <h1 className="m-0 truncate font-headline-md text-headline-md text-on-surface">
              {title}
            </h1>
          )}
        </div>
        {/* Spacer the same size as the back badge so the title stays optically centered. */}
        <div className="h-9 w-9 flex-none" aria-hidden="true" />
      </header>
      <main className="w-full max-w-container-max mx-auto px-md py-lg pb-8 flex flex-col gap-lg">
        {children}
      </main>
    </div>
  );
}
